import { Fragment, useCallback, useEffect, useState } from 'react'
import { SlideOver } from '../components/SlideOver.js'
import { KpiTile } from '../components/KpiTile.js'
import {
  createPartner, createPerson, deactivatePerson, fetchPartners, fetchPeople, updatePartner,
  updatePartnerBranding, updatePerson, uploadPartnerCover, uploadPartnerLogo,
  type PartnerRow, type PartnersResponse, type PeopleResponse, type PersonRow,
} from '../lib/api.js'
import { formatGBP } from '@shared/money'

/**
 * Admin → Partners, and the People at each one. Wave9-admin-only in v1
 * (decision 16).
 *
 * A freshly migrated database has no partners, and without one there is no
 * catalogue and no quotes — the sync only fills the product cache. This page is
 * the thing that turns a deployed portal into a usable one.
 *
 * ⚠️ People are what make PIN login possible at all: auth-request-pin looks the
 * address up in partner_users and refuses anything it can't find. Until this
 * page existed the table had no writer, so Foundation's PIN auth had nobody to
 * authenticate.
 */

const ROLE_HELP: Record<string, string> = {
  owner: 'Runs the account — sees every quote the partner holds.',
  quoter: 'Builds and sends quotes.',
  viewer: 'Read only.',
}

const MODE_HELP: Record<string, string> = {
  agent: 'Wave9 branding, quotes go out at list price, earns a fixed referral fee per accepted quote.',
  reseller: 'Their own branding, they set the sell price above their buy price, Wave9 invoices them.',
  internal: 'Wave9 staff. Fully editable pricing, ad-hoc lines allowed, deals go to the direct pipeline.',
}

export function Partners() {
  const [data, setData] = useState<PartnersResponse | null>(null)
  const [people, setPeople] = useState<PeopleResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<PartnerRow | 'new' | null>(null)
  // Decision 133: the branding editor — issuer identity, contact email, logo.
  const [brandingFor, setBrandingFor] = useState<PartnerRow | null>(null)
  const [person, setPerson] = useState<PersonRow | { partnerId: string } | null>(null)
  const [open, setOpen] = useState<Set<string>>(() => new Set())

  const load = useCallback(() => {
    fetchPartners().then(setData).catch((e: Error) => setError(e.message))
    fetchPeople().then(setPeople).catch((e: Error) => setError(e.message))
  }, [])
  useEffect(load, [load])

  const toggle = (id: string) => setOpen((s) => {
    const next = new Set(s)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  if (error && !data) return <p className="pill pill--stop">{error}</p>
  if (!data) return <p className="muted">Loading partners…</p>

  const live = data.partners.filter((p) => p.active && !p.isTest)
  const peopleFor = (partnerId: string) => people?.people.filter((u) => u.partnerId === partnerId) ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Partners</h1>
          <p>
            Wave9 admin manages these in v1 — partners can't self-administer.
            Each partner's mode decides how their quotes are priced, branded and pushed to HubSpot.
          </p>
        </div>
        {data.editable && (
          <button className="btn btn--primary" onClick={() => setEditing('new')}>New partner</button>
        )}
      </div>

      {data.partners.length === 0 && (
        <div className="banner">
          <strong>No partners yet.</strong>
          <span>
            The catalogue sync only fills the product cache — partners are separate. Until one exists
            there's no catalogue to view and no quotes to build.
          </span>
        </div>
      )}

      <div className="tiles">
        <KpiTile accent label="Active partners" value={String(live.length)} sub={`${data.partners.length} including test and inactive`} />
        <KpiTile label="Resellers" value={String(live.filter((p) => p.mode === 'reseller').length)} />
        <KpiTile label="Agents" value={String(live.filter((p) => p.mode === 'agent').length)} />
        <KpiTile
          label="People"
          value={people ? String(people.people.filter((u) => u.active).length) : '…'}
          sub={people && people.people.some((u) => u.lastLogin) ? 'some have signed in' : 'none have signed in yet'}
        />
      </div>

      <div className="card">
        <div className="card__head">
          <h2>All partners</h2>
          <span className="muted small">{data.partners.length} shown</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Mode</th><th>Tier</th><th>Prefix</th>
                <th className="num">Referral fee</th><th className="num">Quotes</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.partners.map((p) => {
                const staff = peopleFor(p.id)
                const expanded = open.has(p.id)
                return (
                  <Fragment key={p.id}>
                    <tr>
                      <td>
                        <button
                          className="row-toggle"
                          onClick={() => toggle(p.id)}
                          aria-expanded={expanded}
                          aria-controls={`people-${p.id}`}
                        >
                          <span className={`row-toggle__caret ${expanded ? 'row-toggle__caret--on' : ''}`} aria-hidden>▶</span>
                          <strong>{p.name}</strong>
                        </button>
                        <span className="muted small" style={{ marginLeft: 8 }}>
                          {staff.length === 0 ? 'no people' : `${staff.filter((u) => u.active).length} ${staff.filter((u) => u.active).length === 1 ? 'person' : 'people'}`}
                        </span>
                      </td>
                      <td><span className="pill pill--info">{p.mode}</span></td>
                      <td className="muted">{p.tierName ?? (p.mode === 'internal' ? '—' : <span className="pill pill--warn">none</span>)}</td>
                      <td className="small">{p.quotePrefix}</td>
                      <td className="num">{p.referralFee != null ? formatGBP(p.referralFee) : <span className="muted">—</span>}</td>
                      <td className="num">{p.quoteCount}</td>
                      <td>
                        {!p.active && <span className="pill">inactive</span>}
                        {p.active && p.isTest && <span className="pill pill--warn">test</span>}
                        {p.active && !p.isTest && <span className="pill pill--ok">live</span>}
                        {data.editable && (
                          <button className="btn btn--sm btn--ghost" style={{ marginLeft: 8 }} onClick={() => setEditing(p)}>
                            Edit
                          </button>
                        )}
                        {/* Decision 133: resellers get the full issuer editor;
                            agents/internal just the contact + next-steps bits. */}
                        {data.editable && (
                          <button className="btn btn--sm btn--ghost" style={{ marginLeft: 4 }}
                            title={p.mode === 'reseller'
                              ? 'Logo, colours, addresses, contact email — what their customer documents show'
                              : 'Contact email + acceptance next-steps default'}
                            onClick={() => setBrandingFor(p)}>
                            Branding
                          </button>
                        )}
                      </td>
                    </tr>

                    {expanded && (
                      <tr className="people" id={`people-${p.id}`}>
                        <td colSpan={7} className="people__body">
                          <PeopleList
                            partner={p}
                            people={staff}
                            editable={data.editable}
                            onEdit={setPerson}
                            onAdd={() => setPerson({ partnerId: p.id })}
                            onChanged={load}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
              {data.partners.length === 0 && (
                <tr><td colSpan={7} className="muted" style={{ padding: 20 }}>None yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <PartnerPanel
          partner={editing === 'new' ? null : editing}
          tiers={data.tiers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}

      {brandingFor && (
        <BrandingPanel
          partner={brandingFor}
          onClose={() => setBrandingFor(null)}
          onSaved={() => { setBrandingFor(null); load() }}
        />
      )}

      {person && (
        <PersonPanel
          person={'id' in person ? person : null}
          partnerId={person.partnerId}
          partnerName={data.partners.find((p) => p.id === person.partnerId)?.name ?? ''}
          partnerMode={data.partners.find((p) => p.id === person.partnerId)?.mode ?? 'reseller'}
          roles={people?.roles ?? ['owner', 'quoter', 'viewer']}
          onClose={() => setPerson(null)}
          onSaved={() => { setPerson(null); load() }}
        />
      )}
    </>
  )
}

/**
 * The people at one partner. Deliberately a plain table rather than another
 * slide-over list: the common job is "who is at this partner", which should be
 * answerable by expanding a row, not by navigating away.
 */
function PeopleList({
  partner, people, editable, onEdit, onAdd, onChanged,
}: {
  partner: PartnerRow
  people: PersonRow[]
  editable: boolean
  onEdit: (p: PersonRow) => void
  onAdd: () => void
  onChanged: () => void
}) {
  const [busy, setBusy] = useState<string | null>(null)

  const deactivate = async (p: PersonRow) => {
    setBusy(p.id)
    try {
      await deactivatePerson(p.id)
      onChanged()
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <div className="card__head" style={{ padding: '0 0 10px', border: 0 }}>
        <div>
          <h3 style={{ margin: 0 }}>People at {partner.name}</h3>
          <p className="muted small" style={{ margin: '3px 0 0' }}>
            {partner.mode === 'internal'
              ? 'Wave9 staff. They sign in with a PIN emailed to this address.'
              : 'They sign in with a PIN emailed to this address — no password to leak or reset.'}
          </p>
        </div>
        {editable && <button className="btn" onClick={onAdd}>Add person</button>}
      </div>

      {people.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          Nobody yet. Without at least one person, nobody at {partner.name} can sign in.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Role</th><th>Contact number</th>
              <th className="num">Quotes</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {people.map((u) => (
              <tr key={u.id}>
                <td>
                  {u.salutation && <span className="muted">{u.salutation} </span>}
                  <strong>{u.firstName} {u.lastName}</strong>
                </td>
                <td className="small">{u.email}</td>
                <td>
                  <span className="pill pill--info" title={ROLE_HELP[u.role]}>{u.role}</span>
                </td>
                <td className="small">{u.phone ?? <span className="muted">—</span>}</td>
                <td className="num">{u.quoteCount}</td>
                <td>
                  {u.active
                    ? <span className="pill pill--ok">active</span>
                    : <span className="pill">inactive</span>}
                  {u.lastLogin
                    ? <span className="muted small" style={{ marginLeft: 6 }}>signed in</span>
                    : <span className="muted small" style={{ marginLeft: 6 }}>never signed in</span>}
                  {editable && (
                    <>
                      <button className="btn btn--sm btn--ghost" style={{ marginLeft: 8 }} onClick={() => onEdit(u)}>
                        Edit
                      </button>
                      {u.active && (
                        <button
                          className="btn btn--sm btn--danger"
                          style={{ marginLeft: 4 }}
                          disabled={busy === u.id}
                          onClick={() => deactivate(u)}
                          title="Stops them signing in. Their quotes and audit trail stay — nothing is deleted."
                        >
                          {busy === u.id ? '…' : 'Deactivate'}
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

function PersonPanel({
  person, partnerId, partnerName, partnerMode, roles, onClose, onSaved,
}: {
  person: PersonRow | null
  partnerId: string
  partnerName: string
  partnerMode: string
  roles: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = person === null
  const [salutation, setSalutation] = useState(person?.salutation ?? '')
  const [firstName, setFirstName] = useState(person?.firstName ?? '')
  const [lastName, setLastName] = useState(person?.lastName ?? '')
  const [email, setEmail] = useState(person?.email ?? '')
  const [role, setRole] = useState(person?.role ?? 'quoter')
  const [phone, setPhone] = useState(person?.phone ?? '')
  const [hsOwnerId, setHsOwnerId] = useState(person?.hsOwnerId ?? '')
  const [portalRole, setPortalRole] = useState(person?.portalRole ?? 'staff')
  const [active, setActive] = useState(person?.active ?? true)
  const [saving, setSaving] = useState(false)
  const [problems, setProblems] = useState<string[]>([])

  const save = async () => {
    setSaving(true)
    setProblems([])
    try {
      const payload = { salutation: salutation || null, firstName, lastName, email, role, phone: phone || null, hsOwnerId: hsOwnerId || null, portalRole }
      if (isNew) await createPerson({ ...payload, partnerId })
      else await updatePerson({ ...payload, id: person!.id, active })
      onSaved()
    } catch (e) {
      setProblems((e as Error).message.split('\n').filter(Boolean))
      setSaving(false)
    }
  }

  return (
    <SlideOver
      title={isNew ? 'Add person' : `${person!.firstName} ${person!.lastName}`}
      subtitle={partnerName}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn--primary"
            onClick={save}
            disabled={saving || !firstName.trim() || !lastName.trim() || !email.trim()}
          >
            {saving ? 'Saving…' : isNew ? 'Add person' : 'Save'}
          </button>
        </>
      }
    >
      {problems.length > 0 && (
        <ul className="small" style={{ margin: '0 0 16px', paddingLeft: 18, color: 'var(--stop)' }}>
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}

      <label className="small" htmlFor="u-sal">Salutation</label>
      <input id="u-sal" className="input" value={salutation} placeholder="Mr / Mrs / Ms / Dr"
             onChange={(e) => setSalutation(e.target.value)} style={{ width: 160, marginBottom: 14 }} />

      <label className="small" htmlFor="u-first">First name</label>
      <input id="u-first" className="input" value={firstName}
             onChange={(e) => setFirstName(e.target.value)} style={{ width: '100%', marginBottom: 14 }} />

      <label className="small" htmlFor="u-last">Surname</label>
      <input id="u-last" className="input" value={lastName}
             onChange={(e) => setLastName(e.target.value)} style={{ width: '100%', marginBottom: 14 }} />

      <label className="small" htmlFor="u-email">Email</label>
      <input id="u-email" className="input" type="email" autoComplete="off" value={email}
             onChange={(e) => setEmail(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
      <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
        This is how they sign in — the PIN goes here. One address belongs to one person across the
        whole portal, so it can only ever unlock one account.
      </p>

      <label className="small" htmlFor="u-role">Role</label>
      <select id="u-role" className="select" value={role} onChange={(e) => setRole(e.target.value)}
              style={{ width: '100%', marginBottom: 6 }}>
        {roles.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
        {ROLE_HELP[role]} ⚠️ Recorded now, <strong>enforced when RBAC lands</strong> — today every
        signed-in person at a partner can do the same things, so don't treat "viewer" as a control yet.
      </p>

      <label className="small" htmlFor="u-phone">Contact number</label>
      <input id="u-phone" className="input" value={phone} onChange={(e) => setPhone(e.target.value)}
             style={{ width: 220, marginBottom: 6 }} />
      <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
        For Wave9 to reach them. PINs are emailed, never texted.
      </p>

      {partnerMode === 'internal' && (
        <>
          <label className="small" htmlFor="u-prole">Portal access</label>
          <select id="u-prole" className="select" value={portalRole} onChange={(e) => setPortalRole(e.target.value)}
                  style={{ width: 220, marginBottom: 6 }}>
            <option value="admin">Admin</option>
            <option value="staff">Wave9 Staff</option>
            <option value="none">No Access</option>
          </select>
          <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
            What their Microsoft sign-in gets: Admin = full admin session, Wave9 Staff = quoting without the
            Admin panel, No Access = sign-in refused. The roster is the authority; ADMIN_KEY stays as break-glass.
          </p>

          <label className="small" htmlFor="u-hsowner">HubSpot owner ID</label>
          <input id="u-hsowner" className="input" value={hsOwnerId} onChange={(e) => setHsOwnerId(e.target.value)}
                 placeholder="blank = match by email" style={{ width: 220, marginBottom: 6 }} />
          <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
            The deal sync sets the HubSpot deal owner by matching this person's email against
            HubSpot's users. Set an owner ID here only when their login email differs from their HubSpot user.
          </p>
        </>
      )}

      {!isNew && (
        <label className="fields__row" style={{ padding: 0 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active — unticking stops them signing in; nothing is deleted
        </label>
      )}
    </SlideOver>
  )
}

/**
 * A labelled text field. ⚠️ Deliberately OUTSIDE BrandingPanel — defining a
 * component inside another remounts it every render, and a remounted input
 * drops focus after each keystroke.
 */
function Field({ id, label, value, onChange, hint, width = '100%' }: {
  id: string; label: string; value: string; onChange: (v: string) => void; hint?: string; width?: string | number
}) {
  return (
    <>
      <label className="small" htmlFor={id} style={{ display: 'block' }}>{label}</label>
      <input id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)}
             style={{ width, marginBottom: hint ? 6 : 14, display: 'block' }} />
      {hint && <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>{hint}</p>}
    </>
  )
}

/**
 * Decision 133: partner branding — the fields the customer-facing surfaces
 * have read since v1 (issuer block on the quote page/PDF, email colours and
 * sender name, the Reply-To/contact fallback) finally get a writer.
 *
 * Resellers see the full issuer identity; agents and internal partners quote
 * under Wave9's identity, so they only get the contact email (the Reply-To
 * fallback when a quote has no owner, #132) and the acceptance next-steps
 * default. The logo is a real upload (PNG/JPEG, magic-byte checked
 * server-side) — never a pasted URL.
 */
function BrandingPanel({ partner, onClose, onSaved }: {
  partner: PartnerRow
  onClose: () => void
  onSaved: () => void
}) {
  const b = partner.branding ?? {}
  const reseller = partner.mode === 'reseller'
  const [footer, setFooter] = useState(b.footer ?? '')
  const [primary, setPrimary] = useState(b.primary ?? '')
  const [trading, setTrading] = useState(b.trading_address ?? '')
  const [website, setWebsite] = useState(b.website ?? '')
  const [phone, setPhone] = useState(b.phone ?? '')
  const [replyTo, setReplyTo] = useState(b.reply_to ?? '')
  const [legalName, setLegalName] = useState(b.legal_name ?? '')
  const [companyNo, setCompanyNo] = useState(b.company_no ?? '')
  const [regOffice, setRegOffice] = useState(b.registered_office ?? '')
  const [vat, setVat] = useState(b.vat_number ?? '')
  const [nextSteps, setNextSteps] = useState(b.next_steps ?? '')
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoUrl, setLogoUrl] = useState(b.logo_url ?? '')
  // Decision 139: the reseller's quote-pack cover sheet (PDF).
  const [coverFile, setCoverFile] = useState<File | null>(null)
  const [coverUrl, setCoverUrl] = useState(b.cover_url ?? '')
  const [saving, setSaving] = useState(false)
  const [problems, setProblems] = useState<string[]>([])

  const save = async () => {
    setSaving(true)
    setProblems([])
    try {
      const branding: Record<string, string> = reseller
        ? {
            footer, primary, trading_address: trading, website, phone, reply_to: replyTo,
            legal_name: legalName, company_no: companyNo, registered_office: regOffice,
            vat_number: vat, next_steps: nextSteps,
          }
        : { reply_to: replyTo, next_steps: nextSteps }
      await updatePartnerBranding(partner.id, branding)
      if (logoFile) {
        const r = await uploadPartnerLogo(partner.id, logoFile)
        setLogoUrl(r.logoUrl)
      }
      if (coverFile) {
        const r = await uploadPartnerCover(partner.id, coverFile)
        setCoverUrl(r.coverUrl)
      }
      onSaved()
    } catch (e) {
      setProblems((e as Error).message.split('\n').filter(Boolean))
      setSaving(false)
    }
  }

  return (
    <SlideOver
      title={`Branding — ${partner.name}`}
      subtitle={reseller
        ? 'What their customers see on quotes, PDFs and emails'
        : 'Agents and internal quotes carry Wave9 branding — only the contact fields apply'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save branding'}
          </button>
        </>
      }
    >
      {problems.length > 0 && (
        <ul className="small" style={{ margin: '0 0 16px', paddingLeft: 18, color: 'var(--stop)' }}>
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}

      <Field id="b-reply" label="Contact / reply-to email" value={replyTo} onChange={setReplyTo}
        hint="Shown on the quote document and at the foot of every quote email, and used as the Reply-To when a quote has no owner. Leave blank to fall back to the quote owner alone." />

      {reseller && (
        <>
          <Field id="b-footer" label="Display name on quotes" value={footer} onChange={setFooter}
            hint={`How the issuer reads on customer documents. Blank = "${partner.name}".`} />

          <label className="small" htmlFor="b-primary">Brand colour</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <input id="b-primary" className="input" value={primary} placeholder="#1d5c8f"
                   onChange={(e) => setPrimary(e.target.value)} style={{ width: 140 }} />
            <span aria-hidden style={{
              width: 26, height: 26, borderRadius: 6, border: '1px solid var(--line)',
              background: /^#[0-9a-fA-F]{6}$/.test(primary) ? primary : '#006699',
            }} />
          </div>
          <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
            Six-digit hex. Headers, buttons and accents on their customer surfaces. Blank = Wave9 blue.
          </p>

          <label className="small" htmlFor="b-logo">Logo</label>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 6 }}>
            {logoUrl && !logoFile && (
              <img src={logoUrl} alt="Current logo" style={{ maxHeight: 44, maxWidth: 160, border: '1px solid var(--line)', borderRadius: 6, padding: 3 }} />
            )}
            {logoFile && <span className="pill pill--info tiny">{logoFile.name}</span>}
            <input id="b-logo" type="file" accept="image/png,image/jpeg" className="small"
                   onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)} />
          </div>
          <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
            PNG or JPEG, up to 1&nbsp;MB — shown on the customer quote page and PDF. Uploaded when you save.
          </p>

          <label className="small" htmlFor="b-cover">Quote-pack cover page (PDF)</label>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 6 }}>
            {coverUrl && !coverFile && (
              <a className="small" href={coverUrl} target="_blank" rel="noreferrer" title="Open the current cover sheet">current cover ↗</a>
            )}
            {coverFile && <span className="pill pill--info tiny">{coverFile.name}</span>}
            <input id="b-cover" type="file" accept="application/pdf" className="small"
                   onChange={(e) => setCoverFile(e.target.files?.[0] ?? null)} />
          </div>
          <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
            The first page of their customers' Download PDF Pack. ⚠️ Keep the <strong>bottom-right third clear</strong> —
            the quote's facts (number, title, dates) are overlaid there. Without one, their pack simply has
            no cover; it never falls back to Wave9's.
          </p>

          <Field id="b-trading" label="Trading address" value={trading} onChange={setTrading} />
          <Field id="b-web" label="Website" value={website} onChange={setWebsite} width={260} />
          <Field id="b-phone" label="Phone" value={phone} onChange={setPhone} width={220} />

          <h3 className="small" style={{ margin: '4px 0 10px' }}>Legal footer</h3>
          <Field id="b-legal" label="Legal name" value={legalName} onChange={setLegalName}
            hint={`Blank = "${partner.name}".`} />
          <Field id="b-cono" label="Company number" value={companyNo} onChange={setCompanyNo} width={220} />
          <Field id="b-rego" label="Registered office" value={regOffice} onChange={setRegOffice} />
          <Field id="b-vat" label="VAT number" value={vat} onChange={setVat} width={220} />
        </>
      )}

      <label className="small" htmlFor="b-next">Acceptance next steps (default)</label>
      <textarea id="b-next" className="input" rows={3} value={nextSteps}
                onChange={(e) => setNextSteps(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
      <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
        The "what happens next" paragraph on acceptance confirmations, when the quote doesn't set its own.
      </p>

      {reseller && (
        <p className="muted small" style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
          Anything left blank shows its placeholder on the customer document — check a test quote's
          preview after setting these. T&amp;Cs are separate: a reseller's own sets are managed on
          Admin → Organisation, and their quotes can only cite their own (decision 133).
        </p>
      )}
    </SlideOver>
  )
}

function PartnerPanel({
  partner, tiers, onClose, onSaved,
}: {
  partner: PartnerRow | null
  tiers: Array<{ id: string; name: string; multiplier: number }>
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = partner === null
  const [name, setName] = useState(partner?.name ?? '')
  const [mode, setMode] = useState(partner?.mode ?? 'reseller')
  const [tierId, setTierId] = useState(partner?.tierId ?? (tiers[0]?.id ?? ''))
  const [prefix, setPrefix] = useState(partner?.quotePrefix ?? '')
  const [fee, setFee] = useState(partner?.referralFee != null ? String(partner.referralFee) : '')
  const [isTest, setIsTest] = useState(partner?.isTest ?? false)
  const [active, setActive] = useState(partner?.active ?? true)
  const [saving, setSaving] = useState(false)
  const [problems, setProblems] = useState<string[]>([])

  const internal = mode === 'internal'
  const agent = mode === 'agent'

  const save = async () => {
    setSaving(true)
    setProblems([])
    try {
      if (isNew) {
        await createPartner({
          name, mode,
          tierId: internal ? null : tierId,
          quotePrefix: prefix,
          referralFee: agent && fee !== '' ? Number(fee) : null,
          isTest,
        })
      } else {
        await updatePartner({
          id: partner!.id, name,
          tierId: internal ? null : tierId,
          referralFee: agent && fee !== '' ? Number(fee) : null,
          isTest, active,
        })
      }
      onSaved()
    } catch (e) {
      const msg = (e as Error).message
      setProblems(msg.split('\n').filter(Boolean))
      setSaving(false)
    }
  }

  return (
    <SlideOver
      title={isNew ? 'New partner' : partner!.name}
      subtitle={isNew ? undefined : `${partner!.quotePrefix} · ${partner!.quoteCount} quote(s)`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--primary" onClick={save} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : isNew ? 'Create partner' : 'Save'}
          </button>
        </>
      }
    >
      {problems.length > 0 && (
        <ul className="small" style={{ margin: '0 0 16px', paddingLeft: 18, color: 'var(--stop)' }}>
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}

      <label className="small" htmlFor="p-name">Name</label>
      <input id="p-name" className="input" value={name} onChange={(e) => setName(e.target.value)}
             style={{ width: '100%', marginBottom: 14 }} />

      <label className="small" htmlFor="p-mode">Mode</label>
      <select id="p-mode" className="select" value={mode} disabled={!isNew}
              onChange={(e) => setMode(e.target.value as PartnerRow['mode'])}
              style={{ width: '100%', marginBottom: 6 }}>
        <option value="reseller">Reseller</option>
        <option value="agent">Agent</option>
        <option value="internal">Internal (Wave9 staff)</option>
      </select>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
        {MODE_HELP[mode]}
        {!isNew && ' Mode is fixed after creation — it changes how existing quotes price and sync.'}
      </p>

      {!internal && (
        <>
          <label className="small" htmlFor="p-tier">Tier</label>
          <select id="p-tier" className="select" value={tierId} onChange={(e) => setTierId(e.target.value)}
                  style={{ width: '100%', marginBottom: 6 }}>
            {tiers.map((t) => <option key={t.id} value={t.id}>{t.name} (×{t.multiplier})</option>)}
          </select>
          <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
            Scales every Product Line discount. A partner with no tier gets zero discount — safe, but not useful.
          </p>
        </>
      )}

      {isNew && (
        <>
          <label className="small" htmlFor="p-prefix">Quote prefix</label>
          <input id="p-prefix" className="input" value={prefix}
                 onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                 style={{ width: '100%', marginBottom: 6 }} />
          <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
            Quote numbers read <code>{(prefix || 'PREFIX')}-{new Date().getFullYear()}-0001</code>.
            Must be unique, and can't be changed later without renumbering.
          </p>
        </>
      )}

      {agent && (
        <>
          <label className="small" htmlFor="p-fee">Referral fee (£ per accepted quote)</label>
          <input id="p-fee" className="input" type="number" step="0.01" min="0" value={fee}
                 onChange={(e) => setFee(e.target.value)} style={{ width: 160, marginBottom: 6 }} />
          <p className="muted small" style={{ marginTop: 0, marginBottom: 14 }}>
            A fixed fee, not a percentage, overridable per quote. Agent deals carry line
            items at list price — the fee is never netted off.
          </p>
        </>
      )}

      <label className="fields__row" style={{ padding: 0, marginBottom: 8 }}>
        <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />
        Test partner — excluded from the live pipeline, analytics and the commission ledger
      </label>

      {!isNew && (
        <label className="fields__row" style={{ padding: 0 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>
      )}
    </SlideOver>
  )
}
